/**
 * Shared building blocks for the public landing sections — kept in one place so
 * every section renders identical brand chrome (icon set, tone palette, section
 * heading, CTA buttons) and the sign-in / sign-up flow is defined exactly once.
 *
 * `useLandingCta` reuses the SAME auth entry points as the app's route guard:
 *   - sign in  → `auth.signinRedirect({ state: { returnTo } })`
 *   - sign up  → `signupRedirect({ state: { returnTo } })` (Keycloak registration)
 * Both preserve `returnTo = current path + query` so a deep-linked visitor lands
 * back where they intended once authenticated.
 */

import { type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';

import { signupRedirect } from '@/auth/oidc';
import { ICONS, type IconKey, type Tone } from '@/routes/landing/landing-data';

/**
 * Sign-in / sign-up actions that preserve the visitor's intended destination.
 * Pass an explicit `returnTo` (e.g. a plan CTA landing on the subscription
 * page); omit it to preserve the current path + query so a deep-linked visitor
 * lands back where they were.
 */
export function useLandingCta() {
  const auth = useAuth();
  const current = () => window.location.pathname + window.location.search;
  // `typeof` guard: a bare `onClick={signin}` would otherwise pass the React
  // MouseEvent as `returnTo`; only a real string target is honoured.
  return {
    signin: (returnTo?: string) =>
      void auth.signinRedirect({
        state: { returnTo: typeof returnTo === 'string' ? returnTo : current() },
      }),
    signup: (returnTo?: string) =>
      void signupRedirect({
        state: { returnTo: typeof returnTo === 'string' ? returnTo : current() },
      }),
  };
}

/** Saturated gradient per tone — for icon chips, accents and highlight bars. */
export const TONE_GRADIENT: Record<Tone, string> = {
  indigo: 'from-indigo-500 to-violet-500',
  violet: 'from-violet-500 to-fuchsia-500',
  sky: 'from-sky-500 to-indigo-500',
  emerald: 'from-emerald-500 to-teal-500',
  amber: 'from-amber-500 to-orange-500',
  rose: 'from-rose-500 to-pink-500',
};

/** Soft tinted chip background + on-tint text (light + dark correct). */
export const TONE_SOFT: Record<Tone, string> = {
  indigo: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-300',
  violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-300',
  sky: 'bg-sky-500/10 text-sky-600 dark:text-sky-300',
  emerald: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300',
  amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-300',
  rose: 'bg-rose-500/10 text-rose-600 dark:text-rose-300',
};

export interface IconProps {
  name: IconKey;
  className?: string;
  /** Stroke width — thinner for large decorative icons. */
  strokeWidth?: number;
}

/** Inline stroke icon rendered from the shared 24×24 path library. */
export function Icon({ name, className = 'h-6 w-6', strokeWidth = 1.75 }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {ICONS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/** Gradient icon chip used across feature cards, personas and capabilities. */
export function IconChip({
  name,
  tone,
  className = '',
}: {
  name: IconKey;
  tone: Tone;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-token-lg bg-gradient-to-br text-white shadow-elevation-1 ${TONE_GRADIENT[tone]} ${className}`}
    >
      <Icon name={name} className="h-6 w-6" strokeWidth={1.9} />
    </span>
  );
}

/** Centered section eyebrow + title + optional subtitle. */
export function SectionHeading({
  eyebrow,
  title,
  subtitle,
  align = 'center',
}: {
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  align?: 'center' | 'left';
}) {
  const alignment = align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-2xl text-left';
  return (
    <div className={alignment}>
      {eyebrow && (
        <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-primary">{eyebrow}</p>
      )}
      <h2 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
        {title}
      </h2>
      {subtitle && <p className="mt-4 text-pretty text-lg leading-relaxed text-muted">{subtitle}</p>}
    </div>
  );
}

type CtaButtonProps = {
  children: ReactNode;
  onClick: () => void;
  className?: string;
};

/** Solid gradient primary CTA (indigo → violet), used app-wide on the landing. */
export function PrimaryCta({ children, onClick, className = '' }: CtaButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-token bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-3 text-base font-semibold text-white shadow-elevation-2 transition hover:brightness-110 hover:shadow-elevation-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg motion-reduce:transition-none ${className}`}
    >
      {children}
    </button>
  );
}

/** Outlined secondary CTA. */
export function SecondaryCta({ children, onClick, className = '' }: CtaButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-token border border-border bg-surface px-6 py-3 text-base font-semibold text-foreground shadow-elevation-1 transition hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg motion-reduce:transition-none ${className}`}
    >
      {children}
    </button>
  );
}

/** Small Karexia brand mark — a gradient rounded square with a spark glyph. */
export function BrandMark({ className = 'h-9 w-9' }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex items-center justify-center rounded-token bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-elevation-1 ${className}`}
    >
      <Icon name="sparkles" className="h-5 w-5" strokeWidth={2} />
    </span>
  );
}
